import { useEffect, useContext } from "react"
import { Box, Button, Checkbox, Dialog, FormControl, FormControlLabel, Radio, RadioGroup, Typography, Stack } from "@mui/material"
import { ValidateInt, ParsingAmount, ReverseParsingAmount } from "../../../../../core/validator/Validator"
import { env } from "../../../../../core/Env"
import { rqx } from "../../../../../core/request/API"
import { generateVoucher } from "../../../../../core/global/GenerateRandom"
import { SnakeLoader } from "../../../../../core/loader/SnakeLoader"
import { StndrdTextField } from "../../../../../core/styling/form/CstmField"
import { DeleteOutline } from '@mui/icons-material';
import { AddBox1 } from "../../../../../core/global/Icons"
import { ConfirmationModal } from "../../../../../core/global/Confirmation"
import { ToolContext } from "../../../../../core/context/ToolContext"

export const Create = ({ __init }) => {
	const { setup_state, tool_state } = useContext(ToolContext)
    const { __SESSION, userId } = tool_state
    const { memberForm, dfltMemberForm, memberModals, memberSelected, memberMethod, memberLoader, memberError } = setup_state
    const inputs = memberForm.data
	const paymentOptions = [{label: 'Regular Price', name: 'regularPrice'},{label: 'Discount in Amount', name: 'discount'},{label: 'Price', name: 'price'},{label: 'Voucher Code', name: 'voucherCode'}]

	const viewMembership = async () => {
		if (memberMethod.data === 'update') {
			try {
				memberLoader.set(p => ({ ...p, viewForm: true }))
				const s = await rqx.get(`<<tool>>/${env()}/membership/view_membership`, {company_id: __SESSION.data.uaccess[0].company_id, id: memberSelected.data.id})
				const u = await rqx.get(`<<tool>>/${env()}/membership/is_membership_updated`, {company_id: __SESSION.data.uaccess[0].company_id, id: memberSelected.data.id})
				const { data, inclusions } = s
				memberForm.set(p => ({ ...p, 
					code: data[0].series_num,
					name: data[0].name,
					membershipFee: data[0].fee_type === 1 ? 'free' : 'withPay',
					regularPrice: ParsingAmount(data[0].regular_price || '0.00'),
					discount: ParsingAmount(data[0].discount || '0.00'),
					price: ParsingAmount(data[0].price || '0.00'),
					hasVoucherCode: Boolean(data[0].voucher_code),
					voucherCode: data[0].voucher_code || '',
					inclusions: inclusions.map(v => ({ id: v.id, value: v.name, remove: false })),
					membershipValidity: data[0].validity,
					exclusive: Boolean(data[0].is_exclusive),
					hasTransaction: u.has_transaction === "yes"
				}))
				memberSelected.set((p) => ({ ...p, id: data[0].id, active: Boolean(data[0].active) }))
				memberLoader.set(p => ({ ...p, viewForm: false }))
			} catch (error) {
				console.error('Error fetching membership', error);
			}
		} else if (memberMethod.data === 'create') {
			try {
				memberLoader.set(p => ({ ...p, viewForm: true }))
				const s = await rqx.get(`<<tool>>/${env()}/membership/read_series_num`, {company_id: __SESSION.data.uaccess[0].company_id})
				if (s) {
					memberForm.set((p) => ({ ...p, code: s.series_num }))
					memberLoader.set(p => ({ ...p, viewForm: false }))
				}
			} catch (error) {
				console.error(error)
			}
		}
	}
	
    const handleChange = (e) => {
		const { name, value, checked } = e.target;
		if ( name === 'active' ) {
			memberSelected.set((p) => ({ ...p, active: checked }))
			return
		}
		memberForm.set(p => {
			if (['hasVoucherCode', 'exclusive'].includes(name)) {
				return { ...p, [name]: checked, voucherCode: name === 'hasVoucherCode' ? ( checked ? generateVoucher() : '' ) : p.voucherCode };
			}
			if (['regularPrice', 'discount'].includes(name)) {
				let val = value
				if (val.includes('.')) {
					const [intPart, decPart] = val.split('.');
					val = `${intPart}.${decPart.slice(0, 2)}`;
				}
				return { ...p, [name]: val };
			}
			return { ...p, [name]: value };
		});
	};

	const handleBlur = (e) => {
		const { name, value } = e.target;
		memberForm.set(p => ({
			...p,
			[name]: ParsingAmount(ReverseParsingAmount(value)),
			price: ParsingAmount(ReverseParsingAmount(p.regularPrice || 0) - ReverseParsingAmount(p.discount || 0)),
			})
		)
	}

	const handleArrayInputs = (e, k, action) => {
		const updatedForm = {
			...inputs, inclusions: 
				action === 'add' ? [ ...inputs.inclusions, {id: '', value: '', remove: false} ] :
				action === 'remove' ? inputs.inclusions.map((v, i) => { 
					if (i === k) {
						return v.id ? { ...v, remove: true } : null
					}
					return v
				}).filter(v => v !== null) 
				: inputs.inclusions.map((v, i) => i === k ? { ...v, value: e.target.value } : v )
		}
		memberForm.set(updatedForm)
	}
	
    const handleCancel = () => {
		memberModals.set((p) => ({ ...p, form: false }))
        memberForm.set(dfltMemberForm.data)
		memberSelected.set((p) => ({ ...p, id: '', active: false  }))
    }

    const handleSave = () => {
		const errors = {};
        const required = ['name','membershipValidity','inclusions', 'regularPrice']
        required.forEach(field => {
			if (field === 'inclusions') {
				inputs[field].forEach(v => { 
					if (v.value.trim() === '') errors[field] = `${field} is required`;
				})
			} else if (field === 'regularPrice') {
				if (inputs.membershipFee === 'withPay' && !inputs[field]) errors[field] = `${field} is required`
			} else if (field === 'membershipValidity') {
				if (!inputs[field]) errors[field] = `${field} is required`
			} else {
                if (!inputs[field] || inputs[field].trim() === "") errors[field] = `${field} is required`;
            }
        })
        if (Object.keys(errors).length > 0) {
            console.log("Validation failed:", errors);
			memberError.set(true)
            return; // Don't proceed with saving
        }
		memberError.set(false)
		memberModals.set((p) => ({ ...p, confirmation: true }))
    }
	
    const handleClose = () => {
        memberModals.set((p) => ({ ...p, confirmation: false }))
    }
    
    const handleSaving = async() => {
		memberLoader.set((p) => ({ ...p, formSaving: true }))
        const body = {
			id: memberSelected.data.id,
            user_id: userId.data,
            company_id: __SESSION.data.uaccess[0].company_id,
			series_num: inputs.code,
			name: inputs.name,
			fee_type: inputs.membershipFee === 'free' ? 1 : inputs.membershipFee === 'withPay' ? 2 : 3,
			regular_price: inputs.membershipFee === 'withPay' && parseFloat((inputs.regularPrice).replace(/,/g, '')),
			discount: inputs.membershipFee === 'withPay' && parseFloat((inputs.discount).replace(/,/g, '')),
			price: inputs.membershipFee === 'withPay' && parseFloat((inputs.price).replace(/,/g, '')),
			has_voucher_code: inputs.hasVoucherCode ? 1 : 0,
			voucher_code: inputs.voucherCode,
			inclusions: inputs.inclusions,
			validity: inputs.membershipValidity,
			is_exclusive: inputs.exclusive ? 1 : 0,
			platform: 1,
			active: memberSelected.data.active ? 1 : 0,
			data_status: memberSelected.data.active ? 'InUse' : 'IsDeleted',
        }
		const res = await rqx.post(`<<tool>>/${env()}/membership/${memberMethod.data}`, body)
        if (res.message === 'success') {
			await __init(1); // get fresh list from backend
            memberForm.set(dfltMemberForm.data)
			memberSelected.set((p) => ({ ...p, id: '', active: false  }))
			memberLoader.set((p) => ({ ...p, formSaving: false }))
			memberModals.set((p) => ({ ...p, confirmation: false, form: false }))
        }
    }

	useEffect(() => {
		viewMembership();
		// eslint-disable-next-line
	}, [])

    return (
        <>
            <Dialog open={memberModals.data.form} fullWidth PaperProps={{sx: {maxWidth: "700px", minHeight: '500px' }, }}>
				<Stack gap='1.5rem' p='1rem' sx={{ maxHeight: '90vh' }}>
					<Typography variant="h6" textAlign="center" fontWeight={600} fontSize={18} sx={{ color: '#292C2F' }}>
						{ memberMethod.data === 'create' ? 'Create' : 'Update'} Membership Registration
					</Typography>
					{ memberLoader.data.viewForm ? 
						<Stack height='500px' p='1rem' justifyContent='center' alignItems='center'>
							<SnakeLoader size="12px" bg="#11783C" distance="8px"/>
						</Stack>
					:
						<>
							<Stack gap='1rem' className="overflowY" p='0.5rem'>
								<Stack gap='0.5rem' width='100%'>
									<Typography fontSize={14} fontWeight={600} sx={{ color: '#292C2F' }}>Membership Code</Typography>
									<StndrdTextField size='small' disabled value={inputs.code} sx={{ bgcolor: '#fff'}}/>
								</Stack>
								<Stack gap='0.5rem' width='100%'>
									<Stack direction='row' gap='4px'>
										<Typography fontSize={14} fontWeight={600} sx={{ color: '#292C2F' }}>Membership Name</Typography>
										<Typography fontSize={14} fontWeight={600} sx={{ color: '#E34935' }}>*</Typography>
									</Stack>
									<StndrdTextField name="name" size='small' fullWidth value={inputs.name} onChange={handleChange} error={memberError.data && inputs.name.trim() === ''} sx={{ bgcolor: '#fff'}}/>
								</Stack>
								<FormControl>
									<Stack gap='0.25rem'>
										<Stack direction='row' gap='4px'>
											<Typography fontSize={14} fontWeight={600} sx={{ color: '#292C2F' }}>Membership Fee</Typography>
											<Typography fontSize={14} fontWeight={600} sx={{ color: '#E34935' }}>*</Typography>
										</Stack>
										{ inputs.hasTransaction && 
											<Typography fontSize={10} sx={{ color: '#5B6167' }}>Membership fee update not allowed — a transaction already exists.</Typography>
										}
									</Stack>
									<Box display="flex" alignItems="center" justifyContent="flex-start" gap="8px" flexWrap="wrap">
										<RadioGroup sx={{ display: 'flex', flexDirection: 'row' }} onChange={handleChange} value={inputs.membershipFee}>
											<FormControlLabel value="free" name="membershipFee"  control={<Radio size="small" disabled={inputs.hasTransaction}/>} label={<Typography fontSize={14} sx={{ color: '#292C2F' }}>Free</Typography>}/>
											<FormControlLabel value="withPay" name="membershipFee"  control={<Radio size="small" disabled={inputs.hasTransaction}/>} label={<Typography fontSize={14} sx={{ color: '#292C2F' }}>With Pay</Typography>}/>
										</RadioGroup>
									</Box>
								</FormControl>
								{ inputs.membershipFee === 'withPay' &&
									<Stack gap='0.5rem' direction='row'>
										{ paymentOptions.map((v,k) => (
											<Stack key={k} gap='0.5rem'>
												<Stack direction='row' alignItems='center' gap='4px'>
													{ v.name === 'voucherCode' &&
														<Checkbox name='hasVoucherCode' size="small" disabled={inputs.hasTransaction} checked={inputs.hasVoucherCode} onChange={handleChange} sx={{ p: '0rem' }}/>
													}
													<Typography key={k} fontSize={14} align="left" sx={{ color: '#292C2F', flex: 1 }}>{v.label}</Typography>
												</Stack>
												<StndrdTextField name={v.name} disabled={v.name === 'voucherCode' || v.name === 'price' || inputs.hasTransaction} size='small' fullWidth value={inputs[v.name]} onChange={handleChange} {...(v.name !== 'voucherCode' && { onKeyDown: ValidateInt })} {...((v.name === 'regularPrice' || v.name === 'discount') && { onBlur: handleBlur })} error={v.name === 'regularPrice' && (memberError.data && !inputs[v.name])} sx={{ bgcolor: '#fff'}}/>
											</Stack>
										)) }
									</Stack>
								}
								<Stack gap='0.5rem' width='100%'>
									<Stack direction='row' justifyContent='space-between' alignItems='center'>
										<Stack direction='row' gap='4px'>
											<Typography fontSize={14} fontWeight={600}>Inclusion</Typography>
											<Typography fontSize={14} fontWeight={600} sx={{ color: '#E34935' }}>*</Typography>
										</Stack>
										<Box onClick={() => handleArrayInputs(null, null, 'add')} display='flex' alignItems='center' sx={{ cursor: 'pointer', borderRadius: '8px', transition: '0.25s', ':hover': { bgcolor: '#E7E8EA' } }}>
											<AddBox1 stroke='#003F2E' width={20} height={20}/>
										</Box>
									</Stack>
									<Stack gap='0.5rem' width='100%'>
									{inputs.inclusions?.map((v, k) => (
										!v.remove &&
										<Stack key={k} width='100%' direction='row' alignItems="center" gap='0.5rem'>
											<StndrdTextField size='small' value={v.value} onChange={(e) => handleArrayInputs(e, k, 'change_text')} error={memberError.data && v.value.trim() === ''} sx={{ bgcolor: '#fff', flex: 1 }} multiline inputProps={{ style: { maxHeight: "15rem" } }}/>
											{ k !== 0 &&
												<DeleteOutline onClick={() => handleArrayInputs (null, k, 'remove')} sx={{ cursor: 'pointer', transition: '0.25s', ':hover': { color: '#E34935' } }} />
											}
										</Stack>
									))}
									</Stack>
								</Stack>
								<Stack gap='0.5rem' width='100%'>
									<Stack direction='row' gap='4px'>
										<Typography fontSize={14} fontWeight={600} sx={{ color: '#292C2F' }}>Membership Validity (Months)</Typography>
										<Typography fontSize={14} fontWeight={600} sx={{ color: '#E34935' }}>*</Typography>
									</Stack>
									<StndrdTextField name="membershipValidity" size='small' fullWidth value={inputs.membershipValidity} onChange={handleChange} error={memberError.data && !inputs.membershipValidity} onKeyDown={ValidateInt} sx={{ bgcolor: '#fff'}}/>
								</Stack>
								<Stack gap='0.25rem'>
									<Stack alignItems="center" direction='row' gap='0.5rem'>
										<Checkbox name="active" size="small" checked={memberSelected.data.active} onChange={handleChange} sx={{ p: '0.25rem' }}/>
										<Typography fontSize={14} sx={{ color: '#292C2F' }}>Active</Typography>
									</Stack>
									<Stack alignItems="center" direction='row' gap='0.5rem'>
										<Checkbox name="exclusive" size="small" checked={inputs.exclusive} onChange={handleChange} sx={{ p: '0.25rem' }}/>
										<Typography fontSize={14} sx={{ color: '#292C2F' }}>Exclusive</Typography>
									</Stack>
								</Stack>
							</Stack>
							<Stack display="flex" justifyContent="flex-end" alignItems='center' direction='row' gap='1rem' p='0.5rem'>
								<Button onClick={handleCancel} variant="text" sx={{ fontWeight: '400', fontSize: '14px', alignSelf: 'center', bgcolor: '#F7F8F8', color: '#292C2F', p: '0.75rem 2.5rem', boxShadow: 'none', border: 'none', borderRadius: '6px', '&:hover': { boxShadow: 'none' } }}>Cancel</Button>
								<Button onClick={handleSave} variant="contained" sx={{ fontWeight: '400', fontSize: '14px', alignSelf: 'center', color: '#fff', p: '0.75rem 2.5rem', boxShadow: 'none', border: 'none', borderRadius: '6px', '&:hover': { bgcolor: '#003022', boxShadow: 'none' } }}>{memberMethod.data === 'create' ? 'Save' : 'Update'}</Button>
							</Stack>
						</>
					}
				</Stack>
            </Dialog>
            { memberModals.data.confirmation && <ConfirmationModal title={`Confirm ${memberMethod.data === 'create' ? 'Save' : 'Update'}`} cancelFunc={handleClose} sendFunc={handleSaving} text={`${memberMethod.data === 'create' ? 'Save' : 'Update'} this membership registration`} saving={memberLoader.data.formSaving} saveTxt={memberMethod.data === 'create' ? 'Save' : 'Update'}/> }
        </>
    )
}
